import { z } from "zod";

import type { CameraPlan, RouteGeometry } from "./cameraPlan";
import { routeLengthMeters } from "./cameraRoute";
import { resolveFrameProgressions } from "./cameraTrack";
import { type ClimbCategory, climbAt, detectClimbs } from "./climbs";
import {
  bandValueAt,
  type ElevationPoint,
  elevationPointSchema,
  type InfoBand,
  nearestPointIndexAt,
} from "./elevation";

/**
 * profileDesignSchema names the live profile overlay designs the wizard offers —
 * `graph` (the full Tour-de-France elevation curve with a moving readout band),
 * `strip` (a compact stats band, no curve), and `minimal` (a single
 * distance-to-go chip that stays out of the frame). Zod-first because the value
 * is stamped onto the video export profile state and shared with the design-set
 * tool input.
 */
export const profileDesignSchema = z.enum(["graph", "strip", "minimal"]);

/**
 * ProfileDesign names a live profile overlay design, derived from
 * profileDesignSchema.
 */
export type ProfileDesign = z.infer<typeof profileDesignSchema>;

/**
 * infoBandStringSchema validates one categorical analysis band on the distance
 * axis — a surface or way-type stretch — mirroring the hand-written InfoBand<string>
 * shape ({ startKm, endKm, value }). Zod-first because the bands persist in the
 * video profile source the route-set tool carries into the store, so the shape is
 * validated at that boundary rather than trusted.
 */
const infoBandStringSchema = z.object({
  endKm: z.number(),
  startKm: z.number(),
  value: z.string(),
});

/**
 * infoBandNumberSchema is the numeric-band counterpart (a slope-angle stretch),
 * mirroring InfoBand<number>. The two band schemas exist so profileSourceSchema can
 * validate the categorical and slope analyses the overlay reads per frame.
 */
const infoBandNumberSchema = z.object({
  endKm: z.number(),
  startKm: z.number(),
  value: z.number(),
});

/**
 * segmentSpanSchema places one itinerary segment on the cumulative distance axis:
 * the segment's display `name`, its `speed` (m/s, null when the segment never
 * carried the metric), and the [startKm, endKm] stretch it spans. The overlay reads
 * the speed + segment name under the moving flight position from these, the band
 * counterpart for the per-segment channels the index-ranged analyses do not carry.
 */
export const segmentSpanSchema = z.object({
  endKm: z.number(),
  name: z.string(),
  speed: z.number().nullable(),
  startKm: z.number(),
});

/**
 * SegmentSpan is one segment's stretch on the distance axis, derived from
 * segmentSpanSchema.
 */
export type SegmentSpan = z.infer<typeof segmentSpanSchema>;

/**
 * profileSourceSchema is the plan-independent analysis the live profile overlay
 * resolves a per-frame sample from: the `elevationPoints` series, the categorical
 * `surfaceBands` / `waytypeBands`, the numeric `slopeBands`, and the per-segment
 * `segmentSpans` (name + speed). It is derived from the wizard's itinerary detail
 * once (in the route-sync hook, via buildElevationPoints + buildInfoBands) and
 * stored alongside the route, because the full ItineraryDetail is not reachable at
 * record time — the resolver reads this frozen source instead. Zod-first because it
 * crosses the route-set tool boundary into the store and is validated there.
 */
export const profileSourceSchema = z.object({
  elevationPoints: z.array(elevationPointSchema),
  segmentSpans: z.array(segmentSpanSchema),
  slopeBands: z.array(infoBandNumberSchema),
  surfaceBands: z.array(infoBandStringSchema),
  waytypeBands: z.array(infoBandStringSchema),
});

/**
 * ProfileSource is the plan-independent analysis the overlay samples per frame,
 * derived from profileSourceSchema.
 */
export type ProfileSource = z.infer<typeof profileSourceSchema>;

/**
 * ProfileSample is the overlay's readout at one frame, aligned with the camera
 * pose and the route marker at the same index: the cumulative `distanceKm` along
 * the route, the `elevation` (m) and `slopeDeg` there, the running `ascentM` (total
 * positive climb, m, from the route start to this point), the `surface` and
 * `waytype` categories under the flight position, the segment `speed` (m/s) and
 * `segmentName`, the categorized climb the frame sits on (`climbCategory`, its
 * average grade `climbAvgGradePercent`, and `climbEndKm` — the summit's distance,
 * so a renderer reads "to summit" as `climbEndKm - distanceKm`; all three null
 * off-climb), and `progress` (0..1 of the way through the flight, the frame's
 * fraction). Every channel except distance/progress is nullable: a 2D route carries
 * no elevation (so no ascent and no climbs), a gap between bands carries no
 * category, and a segment may lack a speed. It is a hand-written interface (not
 * schema-derived) because it is a trusted in-process value the overlay renderer
 * reads per frame, never parsed at a boundary — the source it is built from carries
 * the Zod guard.
 */
export interface ProfileSample {
  ascentM: number | null;
  climbAvgGradePercent: number | null;
  climbCategory: ClimbCategory | null;
  climbEndKm: number | null;
  distanceKm: number;
  elevation: number | null;
  progress: number;
  segmentName: string | null;
  slopeDeg: number | null;
  speed: number | null;
  surface: string | null;
  waytype: string | null;
}

/**
 * spanAt returns the segment span whose [startKm, endKm] stretch contains the given
 * cumulative distance (km), or null when none does. It mirrors bandValueAt's
 * containment rule — start inclusive, end exclusive, the last span claiming its own
 * end — so the per-segment speed + name resolve under the same boundary handling as
 * the categorical bands.
 */
const spanAt = (spans: SegmentSpan[], distanceKm: number): SegmentSpan | null => {
  for (let index = 0; index < spans.length; index += 1) {
    const span = spans[index];
    if (!span) {
      continue;
    }

    const isLast = index === spans.length - 1;
    if (
      distanceKm >= span.startKm &&
      (distanceKm < span.endKm || (isLast && distanceKm <= span.endKm))
    ) {
      return span;
    }
  }

  return null;
};

/**
 * buildAscentPrefix precomputes the cumulative positive ascent (m) up to each
 * elevation point: ascentPrefix[i] is the sum of every positive elevation delta from
 * the route start through point i, so a single index read yields the running D+ at
 * that point. The points are one continuous head-to-tail axis (buildElevationPoints
 * walks the whole segment chain), so cross-segment deltas count too. Descents
 * contribute zero. Built once before the frame loop so the per-frame ascent is an
 * array read, not a re-walk of the series.
 */
const buildAscentPrefix = (points: ElevationPoint[]): number[] => {
  const prefix = new Array<number>(points.length);
  let total = 0;
  for (let index = 0; index < points.length; index += 1) {
    if (index > 0) {
      const delta = (points[index]?.elevation ?? 0) - (points[index - 1]?.elevation ?? 0);
      if (delta > 0) {
        total += delta;
      }
    }

    prefix[index] = total;
  }

  return prefix;
};

/**
 * resolveProfileSamples produces one ProfileSample per frame, index-aligned with
 * resolveCameraTrack and resolveRouteMarkers by consuming the same per-frame
 * fraction sequence (resolveFrameProgressions) — so samples[i] is the overlay
 * readout for pose[i] and marker[i]. Each frame's cumulative distance is the
 * fraction times the route's total length (cursorAtFraction samples arc-length
 * linearly in the fraction, so this distance lines up with the bands' cumulative km
 * the same way the marker's position does). It resolves the nearest elevation index
 * once per frame (nearestPointIndexAt, a binary search) and reads both the elevation
 * and the running ascent (the precomputed ascentPrefix) from that same index, so the
 * two never disagree; slope / surface / way-type come from the bands at that distance
 * (bandValueAt), and speed + segment name from the segment span. The frame's fraction
 * rides along as `progress`, and ascent is null when the route carries no elevation
 * points. Render-free and pure, so the live preview overlay and the recorder
 * composite read identical samples from (plan, route, source).
 */
export const resolveProfileSamples = (
  plan: CameraPlan,
  route: RouteGeometry,
  source: ProfileSource,
): ProfileSample[] => {
  const fractions = resolveFrameProgressions(plan);
  const routeKm = routeLengthMeters(route.line) / 1_000;

  // Precompute the cumulative-ascent prefix once so each frame reads the running D+
  // from the same index its elevation comes from — never recomputing the climb.
  const points = source.elevationPoints;
  const ascentPrefix = buildAscentPrefix(points);

  // Detect the categorized climbs once before the frame loop; each frame then
  // resolves its containing span with a cheap ordered scan, so the badge and the
  // "to summit" readout never re-walk the elevation series per frame.
  const climbs = detectClimbs(points);

  return fractions.map((fraction) => {
    const distanceKm = fraction * routeKm;
    const index = nearestPointIndexAt(points, distanceKm);
    const span = spanAt(source.segmentSpans, distanceKm);
    const climb = climbAt(climbs, distanceKm);

    return {
      // Elevation and ascent share the one resolved index so a frame's altitude and
      // its running climb are read from the same point; a route with no elevation
      // points yields index -1, leaving both null.
      ascentM: index < 0 ? null : (ascentPrefix[index] ?? null),
      climbAvgGradePercent: climb?.avgGradePercent ?? null,
      climbCategory: climb?.category ?? null,
      climbEndKm: climb?.endKm ?? null,
      distanceKm,
      elevation: index < 0 ? null : (points[index]?.elevation ?? null),
      progress: fraction,
      segmentName: span?.name ?? null,
      slopeDeg: bandValueAt(source.slopeBands, distanceKm),
      speed: span?.speed ?? null,
      surface: bandValueAt(source.surfaceBands, distanceKm),
      waytype: bandValueAt(source.waytypeBands, distanceKm),
    };
  });
};

/** InfoBand re-exported so overlay-source builders can shape bands without a second import. */
export type { InfoBand };
